import React, { useEffect, useMemo, useCallback, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames/bind';
import styles from './Members.scss';
import PropTypes from 'prop-types';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownLink,
} from '@miroculus/nucleo';
import { LoadingSpinner } from '@miroculus/miroculus-ui';
import { NEW_MEMBER_URL, editMemberUrl } from 'cons/routes';
import { useConfirm, DynamicTable } from 'components';
import {
  loadWorkspaceMembers,
  loadPendingInvites,
  removeInvite,
  MEMBER_ROLES,
} from 'reduxModules/workspaces';
import {
  membersSelector,
  getCurrentWorkspace,
  getLoadingMembers,
} from 'reduxModules/workspaces/selectors';

const cx = classNames.bind(styles);

const COLUMN_NAMES = ['Name', 'Status', 'Role'];

const MemberDropdown = ({ id }) => (
  <Dropdown button="...">
    <DropdownLink to={editMemberUrl(id)}>Edit</DropdownLink>
  </Dropdown>
);

const InviteDropdown = ({ id, email, onRemove }) => {
  const { confirm } = useConfirm();

  const handleRemove = () => {
    confirm(
      `Are you sure you want to remove the invitation for ${email}?`,
      () => onRemove(id)
    );
  };

  return (
    <Dropdown button="...">
      <DropdownItem onClick={handleRemove}>Remove</DropdownItem>
    </Dropdown>
  );
};

const MemberItem = ({
  name,
  id,
  email,
  status,
  role,
  onRemove,
  canEdit = true,
}) => (
  <tr data-testid={!onRemove ? 'member' : 'pending-invite'}>
    <td>
      <div className={cx('avatar-name')}>
        <div className={cx('avatar')} />
        <div>
          <p className={cx('name')}>{name}</p>
          <p className={cx('email')}>{email}</p>
        </div>
      </div>
    </td>
    <td>{status}</td>
    <td>{MEMBER_ROLES[role]}</td>
    <td>
      {onRemove ? (
        <InviteDropdown id={id} email={email} onRemove={onRemove} />
      ) : canEdit ? (
        <MemberDropdown id={id} />
      ) : undefined}
    </td>
  </tr>
);

MemberItem.propTypes = {
  name: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  email: PropTypes.string,
  status: PropTypes.string,
  role: PropTypes.string,
  onRemove: PropTypes.func,
  canEdit: PropTypes.bool,
};

export function Members() {
  const dispatch = useDispatch();

  const members = useSelector(membersSelector);
  const loadingMembers = useSelector(getLoadingMembers);
  const currentUser = useSelector((state) => state.auth.user.email);
  const currentWorkspace = useSelector(getCurrentWorkspace);
  const pendingInvites = useSelector((state) => state.workspaces.pendingInvites);

  const handleWorkspaceChange = useCallback(() => {
    dispatch(loadWorkspaceMembers());
  }, [dispatch]);

  const handleInviteRemove = useCallback(
    async (id) => {
      await dispatch(removeInvite(id));
      await dispatch(loadPendingInvites());
    },
    [dispatch]
  );

  const handleMount = useCallback(() => {
    dispatch(loadPendingInvites());
  }, [dispatch]);

  useEffect(() => {
    handleWorkspaceChange();
  }, [currentWorkspace, handleWorkspaceChange]);

  useEffect(() => {
    handleMount();
  }, [handleMount]);

  const memberRowsAndActions = useMemo(
    () =>
      members.map(({ id, email, name, active, role }) => (
        <MemberItem
          key={id}
          id={id}
          name={name}
          email={email}
          status={active ? 'Active' : 'Inactive'}
          role={role}
          canEdit={email !== currentUser}
        />
      )),
    [members, currentUser]
  );

  const pendingInviteRowsAndActions = useMemo(
    () =>
      pendingInvites.map(({ id, email, role }) => (
        <MemberItem
          key={email}
          id={id}
          name={email}
          email={email}
          status="Invited"
          role={role}
          onRemove={handleInviteRemove}
        />
      )),
    [pendingInvites, handleInviteRemove]
  );

  return (
    <div className={cx('container')}>
      {loadingMembers ? (
        <LoadingSpinner message="Loading users..." />
      ) : (
        <>
          <div className={cx('title')}>
            <h1>Members</h1>
            <Button to={NEW_MEMBER_URL} size="small">
              <span aria-hidden>+</span> New member
            </Button>
          </div>
          <DynamicTable
            columnNames={COLUMN_NAMES}
            body={[memberRowsAndActions, pendingInviteRowsAndActions]}
            withActions
          />
        </>
      )}
    </div>
  );
}

export default memo(Members);
